import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { BsFilePdf, BsDownload } from 'react-icons/bs';
import { FiUploadCloud } from 'react-icons/fi';
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import SEO from '../components/SEO';
import { pdfWatermarkSchemas } from '../schemas';
import './ToolPage.css';

const PDFWatermark = () => {
  const [file, setFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Watermark settings
  const [watermarkMode, setWatermarkMode] = useState('text'); // 'text' or 'image'
  const [watermarkText, setWatermarkText] = useState('CONFIDENTIAL');
  const [watermarkImage, setWatermarkImage] = useState(null);
  const [watermarkImageFile, setWatermarkImageFile] = useState(null);
  const [fontSize, setFontSize] = useState(50);
  const [imageSize, setImageSize] = useState(100); // for image watermark
  const [opacity, setOpacity] = useState(0.3);
  const [rotation, setRotation] = useState(45);
  const [color, setColor] = useState('#FF0000');
  const [positionX, setPositionX] = useState(50); // 0-100% horizontal
  const [positionY, setPositionY] = useState(50); // 0-100% vertical
  const [selectedPages, setSelectedPages] = useState([]); // pages to apply watermark
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const togglePageSelection = (pageNum) => {
    setSelectedPages(prev => 
      prev.includes(pageNum) 
        ? prev.filter(p => p !== pageNum)
        : [...prev, pageNum].sort((a, b) => a - b)
    );
  };

  const selectAllPages = () => {
    setSelectedPages(Array.from({ length: totalPages }, (_, i) => i + 1));
  };

  const deselectAllPages = () => {
    setSelectedPages([]);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setWatermarkImage(event.target.result);
        setWatermarkImageFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const generatePreview = async (pdfFile) => {
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      
      // Render first page
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      const imageUrl = canvas.toDataURL('image/png');
      setPreviewImage(imageUrl);
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      await loadPDF(selectedFile);
    }
  };

  const loadPDF = async (pdfFile) => {
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const pageCount = pdf.getPageCount();
      
      setFile(pdfFile);
      setPdfDoc(pdf);
      setTotalPages(pageCount);
      
      // Select all pages by default
      setSelectedPages(Array.from({ length: pageCount }, (_, i) => i + 1));
      
      // Generate preview of first page
      await generatePreview(pdfFile);
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Error loading PDF. Please try again.');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      await loadPDF(droppedFile);
    }
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16) / 255,
      g: parseInt(result[2], 16) / 255,
      b: parseInt(result[3], 16) / 255
    } : { r: 1, g: 0, b: 0 };
  };

  const addWatermark = async () => {
    if (!pdfDoc) {
      alert('Please upload a PDF');
      return;
    }
    
    if (selectedPages.length === 0) {
      alert('Please select at least one page');
      return;
    }
    
    if (watermarkMode === 'text' && !watermarkText.trim()) {
      alert('Please enter watermark text');
      return;
    }
    
    if (watermarkMode === 'image' && !watermarkImage) {
      alert('Please upload a watermark image');
      return;
    }

    setIsProcessing(true);

    try {
      // Create a new PDF document by loading the original file again
      const arrayBuffer = await file.arrayBuffer();
      const newPdf = await PDFDocument.load(arrayBuffer);
      
      const pages = newPdf.getPages();

      if (watermarkMode === 'text') {
        // Text watermark
        const font = await newPdf.embedFont(StandardFonts.HelveticaBold);
        const colorRgb = hexToRgb(color);

        for (let i = 0; i < pages.length; i++) {
          const pageNum = i + 1;
          
          // Only apply to selected pages
          if (!selectedPages.includes(pageNum)) continue;
          
          const page = pages[i];
          const { width, height } = page.getSize();
          const textWidth = font.widthOfTextAtSize(watermarkText, fontSize);

          const x = (width * positionX / 100) - (textWidth / 2);
          const y = height * positionY / 100;

          page.drawText(watermarkText, {
            x,
            y,
            size: fontSize,
            font,
            color: rgb(colorRgb.r, colorRgb.g, colorRgb.b),
            rotate: degrees(rotation),
            opacity: opacity
          });
        }
      } else {
        // Image watermark
        const imageBytes = await fetch(watermarkImage).then(res => res.arrayBuffer());
        let embeddedImage;
        
        if (watermarkImageFile.type === 'image/png') {
          embeddedImage = await newPdf.embedPng(imageBytes);
        } else {
          embeddedImage = await newPdf.embedJpg(imageBytes);
        }

        for (let i = 0; i < pages.length; i++) {
          const pageNum = i + 1;
          
          // Only apply to selected pages
          if (!selectedPages.includes(pageNum)) continue;
          
          const page = pages[i];
          const { width, height } = page.getSize();
          
          const x = (width * positionX / 100) - (imageSize / 2);
          const y = (height * positionY / 100) - (imageSize / 2);

          page.drawImage(embeddedImage, {
            x,
            y,
            width: imageSize,
            height: imageSize,
            rotate: degrees(rotation),
            opacity: opacity
          });
        }
      }

      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `watermarked-${file.name}`;
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error adding watermark:', error);
      alert('Error adding watermark. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="tool-page">
      <SEO 
        title="Add Watermark to PDF Online Free – PDF Watermark"
        description="Add text or image watermarks to PDF files online free. Protect your documents with a custom PDF watermark in seconds."
        keywords="add watermark to pdf, pdf watermark tool, watermark pdf online"
        path="/pdf-watermark"
        schema={pdfWatermarkSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div 
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFilePdf />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Add Watermark to PDF
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Add text watermarks to your PDF documents with custom styling
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          {!file ? (
            <motion.div
              className={`upload-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUploadCloud className="upload-icon" />
              <h3>Drop PDF file here or click to browse</h3>
              <p>Add a watermark to protect your document</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </motion.div>
          ) : (
            <div className="pdf-editor-layout">
              <div className="watermark-split-layout">
                <div className="watermark-preview">
                  <h3>Preview</h3>
                  <div className="pdf-preview-canvas">
                    {previewImage ? (
                      <>
                        <img 
                          src={previewImage} 
                          alt="PDF Page 1" 
                          className="pdf-page-preview"
                        />
                        {watermarkMode === 'text' ? (
                          <div 
                            className="watermark-preview-text"
                            style={{
                              fontSize: `${fontSize * 0.5}px`,
                              opacity: opacity,
                              color: color,
                              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                              position: 'absolute',
                              top: `${positionY}%`,
                              left: `${positionX}%`,
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold',
                              pointerEvents: 'none'
                            }}
                          >
                            {watermarkText}
                          </div>
                        ) : watermarkImage && (
                          <img
                            src={watermarkImage}
                            alt="Watermark"
                            className="watermark-preview-image"
                            style={{
                              opacity: opacity,
                              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                              position: 'absolute',
                              top: `${positionY}%`,
                              left: `${positionX}%`,
                              width: `${imageSize}px`,
                              height: 'auto',
                              pointerEvents: 'none'
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="preview-loading">Loading preview...</div>
                    )}
                  </div>
                  <p className="preview-note">
                    Watermark will be applied to all {totalPages} pages
                  </p>
                </div>

                <div className="watermark-controls">
                  <h3>Watermark Settings</h3>

                  <div className="watermark-mode-tabs">
                    <button
                      className={`mode-tab ${watermarkMode === 'text' ? 'active' : ''}`}
                      onClick={() => setWatermarkMode('text')}
                    >
                      Text
                    </button>
                    <button
                      className={`mode-tab ${watermarkMode === 'image' ? 'active' : ''}`}
                      onClick={() => setWatermarkMode('image')}
                    >
                      Image
                    </button>
                  </div>

                  {watermarkMode === 'text' ? (
                    <>
                      <div className="setting-group">
                        <label>Watermark Text</label>
                        <input
                          type="text"
                          value={watermarkText}
                          onChange={(e) => setWatermarkText(e.target.value)}
                          placeholder="Enter watermark text"
                          className="text-input-large"
                        />
                      </div>

                      <div className="setting-group">
                        <label>Font Size: {fontSize}px</label>
                        <input
                          type="range"
                          min="20"
                          max="100"
                          value={fontSize}
                          onChange={(e) => setFontSize(parseInt(e.target.value))}
                          className="slider"
                        />
                      </div>

                      <div className="setting-group">
                        <label>Color</label>
                        <div className="color-picker-wrapper">
                          <input
                            type="color"
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            className="color-picker"
                          />
                          <span className="color-value">{color}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="setting-group">
                        <label>Upload Watermark Image</label>
                        <div className="image-upload-area">
                          {watermarkImage ? (
                            <div className="uploaded-image-preview">
                              <img src={watermarkImage} alt="Watermark" />
                              <button
                                className="btn-remove-image"
                                onClick={() => {
                                  setWatermarkImage(null);
                                  setWatermarkImageFile(null);
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn-upload-image"
                              onClick={() => imageInputRef.current?.click()}
                            >
                              + Choose Image
                            </button>
                          )}
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            style={{ display: 'none' }}
                          />
                        </div>
                      </div>

                      <div className="setting-group">
                        <label>Image Size: {imageSize}px</label>
                        <input
                          type="range"
                          min="50"
                          max="300"
                          value={imageSize}
                          onChange={(e) => setImageSize(parseInt(e.target.value))}
                          className="slider"
                        />
                      </div>
                    </>
                  )}

                  <div className="setting-group">
                    <label>Opacity: {Math.round(opacity * 100)}%</label>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.1"
                      value={opacity}
                      onChange={(e) => setOpacity(parseFloat(e.target.value))}
                      className="slider"
                    />
                  </div>

                  <div className="setting-group">
                    <label>Rotation: {rotation}°</label>
                    <input
                      type="range"
                      min="-180"
                      max="180"
                      value={rotation}
                      onChange={(e) => setRotation(parseInt(e.target.value))}
                      className="slider"
                    />
                  </div>

                  <div className="setting-group">
                    <label>Position X (Left ← → Right): {positionX}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={positionX}
                      onChange={(e) => setPositionX(parseInt(e.target.value))}
                      className="slider"
                    />
                    <div className="quality-labels">
                      <span>Left</span>
                      <span>Right</span>
                    </div>
                  </div>

                  <div className="setting-group">
                    <label>Position Y (Down ↓ ↑ Up): {positionY}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={positionY}
                      onChange={(e) => setPositionY(parseInt(e.target.value))}
                      className="slider"
                    />
                    <div className="quality-labels">
                      <span>Bottom</span>
                      <span>Top</span>
                    </div>
                  </div>

                  <div className="apply-to-pages-section">
                    <h4>Apply to Pages</h4>
                    <div className="page-selection-actions">
                      <button className="btn-secondary-small" onClick={selectAllPages}>
                        Select All
                      </button>
                      <button className="btn-secondary-small" onClick={deselectAllPages}>
                        Clear All
                      </button>
                    </div>
                    <div className="pages-selector-row">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                        <motion.div
                          key={pageNum}
                          className={`page-selector-card ${selectedPages.includes(pageNum) ? 'selected' : ''}`}
                          onClick={() => togglePageSelection(pageNum)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <div className="page-selector-number">{pageNum}</div>
                          {selectedPages.includes(pageNum) && (
                            <div className="page-selector-check">✓</div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                    <div className="selection-summary">
                      Selected: {selectedPages.length} of {totalPages} pages
                    </div>
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <motion.button
                  className="btn-primary-large"
                  onClick={addWatermark}
                  disabled={isProcessing || (watermarkMode === 'text' && !watermarkText.trim()) || (watermarkMode === 'image' && !watermarkImage)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <BsDownload />
                  {isProcessing ? 'Adding Watermark...' : 'Download with Watermark'}
                </motion.button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PDFWatermark;
